<template>
  <div class="style-selector">
    <h4 class="selector-title">选择视觉风格</h4>
    <p class="selector-desc">选择一种风格应用到所有设定图生成</p>
    
    <div class="style-grid">
      <div
        v-for="style in styles"
        :key="style.style_key"
        class="style-card"
        :class="{ 'selected': selectedStyle === style.style_key }"
        @click="selectStyle(style.style_key)"
      >
        <div class="style-thumbnail">
          <img v-if="style.thumbnail_url" :src="style.thumbnail_url" :alt="style.style_name">
          <div v-else class="thumbnail-placeholder">
            <el-icon><Picture /></el-icon>
            <span>暂无预览</span>
          </div>
        </div>
        <div class="style-info">
          <div class="style-name">{{ style.style_name }}</div>
          <div v-if="style.style_name_en" class="style-name-en">{{ style.style_name_en }}</div>
          <div class="style-desc">{{ style.description }}</div>
        </div>
        <div v-if="selectedStyle === style.style_key" class="selected-indicator">
          <el-icon><Check /></el-icon>
        </div>
      </div>
    </div>
    
    <div v-if="loading" class="loading-state">
      <el-icon class="is-loading"><Loading /></el-icon>
      <span>加载风格列表...</span>
    </div>
    
    <div v-if="error" class="error-state">
      <el-icon><Warning /></el-icon>
      <span>{{ error }}</span>
      <el-button type="primary" link @click="fetchStyles">重试</el-button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Picture, Check, Loading, Warning } from '@element-plus/icons-vue'
import api from '../../api/index.js'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const styles = ref([])
const loading = ref(false)
const error = ref('')
const selectedStyle = ref(props.modelValue)

// 监听外部值变化
watch(() => props.modelValue, (newVal) => {
  selectedStyle.value = newVal
})

// 监听内部选择变化
watch(selectedStyle, (newVal) => {
  emit('update:modelValue', newVal)
  emit('change', newVal)
})

// 获取风格列表
const fetchStyles = async () => {
  loading.value = true
  error.value = ''
  
  try {
    const result = await api.get('/styles')

    if (result.success) {
      styles.value = result.data || []
      
      // 如果没有选中任何风格，自动选择第一个
      if (!selectedStyle.value && styles.value.length > 0) {
        selectedStyle.value = styles.value[0].style_key
      }
    } else {
      throw new Error(result.message || '获取风格列表失败')
    }
  } catch (err) {
    error.value = err.message
    ElMessage.error(err.message)
  } finally {
    loading.value = false
  }
}

// 选择风格
const selectStyle = (styleKey) => {
  selectedStyle.value = styleKey
}

// 初始化
onMounted(() => {
  fetchStyles()
})

// 暴露方法
defineExpose({
  fetchStyles,
  getSelectedStyle: () => styles.value.find(s => s.style_key === selectedStyle.value)
})
</script>

<style scoped lang="scss">
.style-selector {
  .selector-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--el-text-color-primary);
    margin: 0 0 8px;
  }
  
  .selector-desc {
    font-size: 14px;
    color: var(--el-text-color-secondary);
    margin: 0 0 16px;
  }
  
  .style-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
  }
  
  .style-card {
    position: relative;
    border: 2px solid var(--el-border-color-light);
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s;
    background: var(--el-bg-color);
    
    &:hover {
      border-color: #409eff;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    &.selected {
      border-color: #409eff;
      box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
    }
    
    .style-thumbnail {
      width: 100%;
      height: 150px;
      overflow: hidden;
      background: var(--el-fill-color-light);
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      
      .thumbnail-placeholder {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: var(--el-text-color-secondary);
        
        .el-icon {
          font-size: 32px;
          margin-bottom: 8px;
        }
        
        span {
          font-size: 12px;
        }
      }
    }
    
    .style-info {
      padding: 12px;
      
      .style-name {
        font-size: 14px;
        font-weight: 600;
        color: var(--el-text-color-primary);
        margin-bottom: 4px;
      }
      
      .style-name-en {
        font-size: 12px;
        color: var(--el-text-color-secondary);
        margin-bottom: 4px;
      }
      
      .style-desc {
        font-size: 12px;
        color: var(--el-text-color-regular);
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }
    
    .selected-indicator {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: #409eff;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .el-icon {
        font-size: 14px;
      }
    }
  }
  
  .loading-state,
  .error-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 40px;
    color: var(--el-text-color-secondary);
    
    .el-icon {
      font-size: 20px;
    }
  }
  
  .error-state {
    color: #f56c6c;
  }
}
</style>
